import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { getInboxSummary, getMyNotifications, notificationActions } from '@/lib/api';
import { inboxPath, inboxError } from '@/lib/inbox';
import useVisiblePolling from '@/hooks/useVisiblePolling';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import NotificationDetail from '@/components/inbox/NotificationDetail';
import ChatWorkspace from '@/components/inbox/ChatWorkspace';

const InboxContext = createContext(null);
export const useInbox = () => useContext(InboxContext);

export function InboxProvider({ children }) {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [recent, setRecent] = useState([]);
  const recentRef = useRef(recent);
  recentRef.current = recent;
  const [revision, setRevision] = useState(0);
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [selection, setSelection] = useState(null);
  const drafts = useRef({});
  const userRef = useRef(profile?.id);
  const dataVersion = useRef(0);
  userRef.current = profile?.id;
  const refreshSummary = useCallback(async () => {
    const user = userRef.current;
    const version = dataVersion.current;
    if (!user) return;
    try {
      const { data } = await getInboxSummary();
      if (userRef.current === user && dataVersion.current === version) setUnreadCount(data.unread_count || 0);
    } catch { return false; }
  }, []);
  const loadRecent = useCallback(async () => {
    const user = userRef.current;
    const version = dataVersion.current;
    const { data } = await getMyNotifications({ limit: 20 });
    if (userRef.current === user && dataVersion.current === version) {
      setRecent(data.items || []);
      setUnreadCount(data.unread_count || 0);
    }
    return data;
  }, []);
  useEffect(() => {
    dataVersion.current += 1;
    setRecent([]); setUnreadCount(0); setSelection(null); setDismissedIds(new Set()); drafts.current = {};
    if (profile?.id) refreshSummary();
  }, [profile?.id, refreshSummary]);
  useVisiblePolling(refreshSummary, 60000, Boolean(profile?.id));
  const action = useCallback(async (data) => {
    const user = userRef.current;
    const version = ++dataVersion.current;
    const ids = new Set(data.ids || []);
    const previous = recentRef.current;
    if (data.action === 'delete') setDismissedIds(current => new Set([...current, ...ids]));
    if (data.action === 'restore') setDismissedIds(current => new Set([...current].filter(id => !ids.has(id))));
    setRecent(rows => rows.flatMap(row => {
      if (!ids.has(row.id)) return [row];
      if (data.action === 'delete') return [];
      return [{ ...row, read_at: data.action === 'read' ? new Date().toISOString() : data.action === 'unread' ? null : row.read_at }];
    }));
    try {
      const { data: result } = await notificationActions(data);
      if (userRef.current === user) {
        const latest = dataVersion.current === version;
        dataVersion.current += 1;
        if (latest) setUnreadCount(result.unread_count || 0);
        else refreshSummary();
        setRevision(value => value + 1);
      }
      return result;
    } catch (error) {
      if (userRef.current === user) {
        dataVersion.current += 1;
        setRecent(previous); refreshSummary();
        if (data.action === 'delete') setDismissedIds(current => new Set([...current].filter(id => !ids.has(id))));
      }
      throw error;
    }
  }, [refreshSummary]);
  const dismiss = useCallback(async (item) => {
    try {
      await action({ action: 'delete', ids: [item.id] });
      toast.success('Notification deleted', { duration:12000, action: { label: 'Undo', onClick: () => action({action:'restore', ids:[item.id]}).catch(error => toast.error(inboxError(error, 'Could not restore notification'))) } });
    } catch (error) { toast.error(inboxError(error, 'Could not delete notification')); }
  }, [action]);
  const open = useCallback(item => setSelection({ notificationId: item.id }), []);
  const close = useCallback(() => setSelection(null), []);
  const openChat = useCallback((channelId, draft = '') => {
    if (draft) drafts.current[`chat:${channelId}`] = draft;
    setSelection({ channelId });
  }, []);
  const expand = () => {
    const params = new URLSearchParams(selection?.channelId ? {view:'messages', conversation:selection.channelId} : {notification:selection?.notificationId || ''});
    navigate(`${inboxPath(profile)}?${params}`); close();
  };
  const value = { unreadCount, recent, revision, dismissedIds, action, dismiss, open, close, openChat, loadRecent, refreshSummary,
    draftFor: key => drafts.current[key] || '', saveDraft: (key, text) => { drafts.current[key] = text; } };
  return <InboxContext.Provider value={value}>
    {children}
    <Sheet open={Boolean(selection)} onOpenChange={value => { if (!value) close(); }}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="border-b px-5 py-4 pr-12">
          <SheetTitle>Inbox</SheetTitle><SheetDescription>Read the context and continue the conversation.</SheetDescription>
          <Button variant="outline" size="sm" className="w-fit" onClick={expand}>Expand to Inbox</Button>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {selection?.notificationId && <NotificationDetail key={selection.notificationId} id={selection.notificationId} onOpenChat={openChat} onNavigate={close} />}
          {selection?.channelId && <ChatWorkspace initialChannelId={selection.channelId} compact />}
        </div>
      </SheetContent>
    </Sheet>
  </InboxContext.Provider>;
}
